/* 31 Nights of Screams. Screaming Saints, October 2026.
 * One file, no dependencies. The calendar builder at the top is pure (no DOM)
 * and is what the node tests exercise; the page code below it only runs in a
 * browser. Film data comes from ss-31-nights-films.json: an append-only list
 * where each film's `n` never changes, because share links store those numbers.
 */
(function (root) {
  'use strict';

  var YEAR = 2026;
  var NIGHTS = 31;
  // What each quiz answer pulls from the library.
  var TASTES = {
    slasher: ['slasher'],
    supernatural: ['supernatural', 'possession'],
    found: ['found-footage'],
    folk: ['folk'],
    creature: ['creature', 'zombie', 'vampire', 'sci-fi'],
    psych: ['psychological'],
    comedy: ['comedy'],
    classic: ['classic'],
  };
  // Eras are a promise, not a preference: if someone rules out the 1940s, no
  // amount of pool-widening may hand them a film from 1942.
  var ERAS = ['pre60', '1960s', '1970s', '1980s', '1990s', '2000s', '2010s', '2020s'];
  var eraOf = function (year) { return year < 1960 ? 'pre60' : Math.floor(year / 10) * 10 + 's'; };

  // Scare level -> the intensity band the month is built from.
  var LEVELS = { cozy: [1, 2], creepy: [1, 3], scary: [2, 4], nomercy: [3, 5] };
  // Ranks are earned by nights survived. Everyone starts a sinner.
  var RANKS = [[31, 'Saint'], [21, 'Martyr'], [13, 'Devout'], [7, 'Novice'], [0, 'Sinner']];

  function rng(seed) { // mulberry32: small, fast, and the same on every device
    var a = seed >>> 0;
    return function () {
      a = (a + 0x6d2b79f5) >>> 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  var weekday = function (night) { return new Date(YEAR, 9, night).getDay(); }; // 0 Sun .. 6 Sat
  var isWeekend = function (night) { var d = weekday(night); return d === 5 || d === 6; };

  function wantedTags(prefs) {
    var tastes = prefs.tastes && prefs.tastes.length ? prefs.tastes : Object.keys(TASTES);
    var tags = {};
    tastes.forEach(function (t) { (TASTES[t] || []).forEach(function (g) { tags[g] = 1; }); });
    if (prefs.level === 'cozy') tags.gateway = 1; // a cozy month needs the family shelf whatever else was ticked
    return tags;
  }

  /* The pool is widened one notch at a time until it can fill a month, and the
   * result says how far it had to go, so the page can be honest about it. */
  function poolFor(films, prefs) {
    var band = LEVELS[prefs.level] || LEVELS.creepy;
    var tags = wantedTags(prefs);
    var hasTag = function (f) { return f.g.some(function (g) { return tags[g]; }); };
    var inBand = function (f, pad) { return f.i >= band[0] - pad && f.i <= band[1] + (prefs.level === 'cozy' ? 0 : pad); };
    var eras = {}, anyEra = true;
    (prefs.eras || []).forEach(function (e) { if (ERAS.indexOf(e) >= 0) { eras[e] = 1; anyEra = false; } });
    var inEra = function (f) { return anyEra || !!eras[eraOf(f.y)]; };
    var steps = [
      { note: '', test: function (f) { return hasTag(f) && inBand(f, 0) && !(prefs.skipSeen && f.f); } },
      { note: 'seen', test: function (f) { return hasTag(f) && inBand(f, 0); } },
      { note: 'band', test: function (f) { return hasTag(f) && inBand(f, 1); } },
      { note: 'any', test: function (f) { return inBand(f, 1); } },
    ];
    var pool;
    for (var s = 0; s < steps.length; s++) {
      pool = films.filter(function (f) { return inEra(f) && steps[s].test(f); });
      if (pool.length >= NIGHTS + 12) return { pool: pool, widened: steps[s].note };
    }
    // Last resort keeps both promises (era, and scare level within a notch).
    // If that still cannot fill a month, the caller has to ask for more decades.
    return { pool: pool, widened: 'any', short: pool.length < NIGHTS };
  }

  // How many films could make it into this month. The quiz shows it live.
  function available(films, prefs) { var got = poolFor(films, prefs); return got.short ? got.pool.length : Math.max(got.pool.length, NIGHTS); }

  // How wrong a film is for a given night. Lowest cost wins.
  function cost(film, night, band, chosen, rand) {
    var target = band[0] + (band[1] - band[0]) * Math.pow(night / NIGHTS, 0.8); // the month climbs
    var c = Math.abs(film.i - target) + rand() * 1.4;
    var prev = chosen[night - 1], prev2 = chosen[night - 2];
    if (prev && prev.g[0] === film.g[0]) c += prev2 && prev2.g[0] === film.g[0] ? 3 : 0.9;
    if (prev && Math.floor(prev.y / 10) === Math.floor(film.y / 10)) c += 0.3;
    if (!isWeekend(night) && film.rt > 112) c += 0.8;   // school night, shorter film
    if (isWeekend(night) && film.i < band[1] - 1) c += 0.4; // weekends carry the heavier ones
    for (var k in chosen) if (chosen[k] && chosen[k].d === film.d) c += 2.5; // one director, one night
    return c;
  }

  function pick(pool, night, band, chosen, used, rand) {
    var best = null, bestCost = Infinity;
    for (var i = 0; i < pool.length; i++) {
      var f = pool[i];
      if (used[f.n]) continue;
      var c = cost(f, night, band, chosen, rand);
      if (c < bestCost) { best = f; bestCost = c; }
    }
    return best;
  }

  function build(films, prefs) {
    var rand = rng(prefs.seed || 1);
    var got = poolFor(films, prefs);
    if (got.short) return { error: 'short', have: got.pool.length };
    var band = LEVELS[prefs.level] || LEVELS.creepy;
    var chosen = {}, used = {};

    // Halloween first: the heaviest film in the pool, and a famous one if allowed.
    var finale = got.pool.slice().sort(function (a, b) {
      return (b.i - a.i) || ((prefs.skipSeen ? a.f - b.f : b.f - a.f)) || (rand() - 0.5);
    })[0];
    chosen[NIGHTS] = finale; used[finale.n] = 1;

    for (var night = 1; night < NIGHTS; night++) {
      var f = pick(got.pool, night, band, chosen, used, rand);
      chosen[night] = f; used[f.n] = 1;
    }
    var nights = [];
    for (var n = 1; n <= NIGHTS; n++) nights.push(chosen[n].n);
    return { nights: nights, widened: got.widened };
  }

  // Replace one night, keeping every other night as it is.
  function swap(films, prefs, nights, night, seed) {
    var byN = index(films), chosen = {}, used = {};
    nights.forEach(function (n, i) { chosen[i + 1] = byN[n]; used[n] = 1; });
    delete chosen[night];
    var got = poolFor(films, prefs);
    var f = pick(got.pool, night, LEVELS[prefs.level] || LEVELS.creepy, chosen, used, rng(seed));
    if (!f) return nights;
    var next = nights.slice(); next[night - 1] = f.n;
    return next;
  }

  function index(films) { var m = {}; films.forEach(function (f) { m[f.n] = f; }); return m; }

  function rank(done) {
    for (var i = 0; i < RANKS.length; i++) if (done >= RANKS[i][0]) return RANKS[i][1];
    return 'Sinner';
  }

  // Share links: #c=<31 film numbers in base 36>. Short enough for a caption.
  var encode = function (nights) { return nights.map(function (n) { return n.toString(36); }).join('.'); };
  function decode(str, films) {
    var byN = index(films);
    var nights = String(str || '').split('.').map(function (s) { return parseInt(s, 36); });
    var seen = {};
    var ok = nights.length === NIGHTS && nights.every(function (n) { if (!byN[n] || seen[n]) return false; seen[n] = 1; return true; });
    return ok ? nights : null;
  }


  /* ---- verdict: every quiz ends with a patron saint ---- */
  var SAINTS = {
    slasher: ['Final Girls', 'You count the exits. You never say you will be right back.'],
    supernatural: ['Haunted Houses', 'You heard it too. You are going upstairs anyway.'],
    found: ['Shaky Cameras', 'You keep filming. Somebody has to.'],
    folk: ['Bad Harvests', 'You were warned about the village. You stayed for the festival.'],
    creature: ['Things With Teeth', 'You want to see the monster. All of it.'],
    psych: ['Unreliable Narrators', 'You are not sure what you saw. That is the part you like.'],
    comedy: ['Nervous Laughter', 'You laugh first. Then you check the back seat.'],
    classic: ['Black and White Nightmares', 'You like your monsters old and your shadows long.'],
    all: ['Whatever Is in the Basement', 'You do not have a type. You go down the stairs every time.'],
  };
  var VOWS = { cozy: 'Keeps one light on.', creepy: 'Lights low, door locked.', scary: 'Checks the locks twice.', nomercy: 'Does not look away.' };

  function verdict(prefs) {
    var tastes = (prefs.tastes || []).filter(function (t) { return SAINTS[t]; });
    var key = 'all';
    if (tastes.length && tastes.length < Object.keys(TASTES).length) key = tastes[Math.floor(rng(prefs.seed || 1)() * tastes.length)];
    return { key: key, title: 'Patron Saint of ' + SAINTS[key][0], line: SAINTS[key][1], vow: VOWS[prefs.level] || VOWS.creepy };
  }

  /* ---- badges: earned from the log, shown on the page and the poster ---- */
  var BADGES = [
    ['first', 'First Blood', 'Survive one night.'],
    ['vigil', 'Vigil', 'Seven nights in a row.'],
    ['half', 'Halfway to Heaven', 'Sixteen nights survived.'],
    ['weekend', 'Lost Weekend', 'A Friday, Saturday and Sunday, all three.'],
    ['time', 'Time Traveler', 'Films from five different decades.'],
    ['judge', 'Hanging Judge', 'Give a film a single halo.'],
    ['blessed', 'Blessed', 'Give five films five halos.'],
  ];

  function badges(log, monthFilms) { // log: {night: {done, halo}}, monthFilms: film per night, index 0 = night 1
    log = log || {};
    var done = function (n) { return !!(log[n] && log[n].done); };
    var count = 0, run = 0, best = 0, decades = {}, ones = 0, fives = 0, weekend = false;
    for (var n = 1; n <= NIGHTS; n++) {
      if (done(n)) {
        count++; run++; best = Math.max(best, run);
        if (monthFilms[n - 1]) decades[Math.floor(monthFilms[n - 1].y / 10)] = 1;
        if (log[n].halo === 1) ones++;
        if (log[n].halo === 5) fives++;
        if (weekday(n) === 0 && done(n - 1) && done(n - 2)) weekend = true;
      } else run = 0;
    }
    var has = { first: count >= 1, vigil: best >= 7, half: count >= 16, weekend: weekend, time: Object.keys(decades).length >= 5, judge: ones >= 1, blessed: fives >= 5 };
    return BADGES.map(function (b) { return { key: b[0], name: b[1], how: b[2], earned: !!has[b[0]] }; });
  }

  /* ---- exports ---- */
  function icsText(s) { return String(s).replace(/\\/g, '\\\\').replace(/;/g, '\;').replace(/,/g, '\\,').replace(/\n/g, '\\n'); }
  function fold(line) { // RFC 5545: lines over 75 octets continue on the next line after a space
    var out = [];
    while (line.length > 73) { out.push(line.slice(0, 73)); line = ' ' + line.slice(73); }
    out.push(line);
    return out.join('\r\n');
  }
  var two = function (n) { return (n < 10 ? '0' : '') + n; };

  // Floating local times (no time zone): 8 PM wherever the phone is.
  function ics(monthFilms, url) {
    var lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Screaming Saints//31 Nights of Screams//EN', 'CALSCALE:GREGORIAN', 'X-WR-CALNAME:31 Nights of Screams'];
    monthFilms.forEach(function (f, i) {
      var night = i + 1, end = 20 * 60 + f.rt, day = night + Math.floor(end / 1440);
      var endStamp = (day > 31 ? YEAR + '1101' : YEAR + '10' + two(day)) + 'T' + two(Math.floor((end % 1440) / 60)) + two(end % 60) + '00';
      lines.push('BEGIN:VEVENT', 'UID:ss31-' + YEAR + '-' + night + '-' + f.n + '@screamingsaints.com', 'DTSTAMP:' + YEAR + '0920T000000Z',
        'DTSTART:' + YEAR + '10' + two(night) + 'T200000', 'DTEND:' + endStamp,
        'SUMMARY:' + icsText((night === 31 ? 'Halloween: ' : 'Night ' + night + ': ') + f.t + ' (' + f.y + ')'),
        'DESCRIPTION:' + icsText(f.w + '\n' + f.rt + ' min. Scare ' + f.i + ' of 5.\nMark it survived and rate it in halos: ' + url),
        'URL:' + url, 'BEGIN:VALARM', 'ACTION:DISPLAY', 'DESCRIPTION:' + icsText('Tonight: ' + f.t), 'TRIGGER:-PT30M', 'END:VALARM', 'END:VEVENT');
    });
    lines.push('END:VCALENDAR');
    return lines.map(fold).join('\r\n') + '\r\n';
  }

  // Letterboxd list import reads Title, Year and Directors and keeps the row order.
  function letterboxdCsv(monthFilms) {
    var q = function (v) { return '"' + String(v).replace(/"/g, '""') + '"'; };
    return ['Title,Year,Directors'].concat(monthFilms.map(function (f) { return [q(f.t), f.y, q(f.d.replace(/ and /g, ', '))].join(','); })).join('\n') + '\n';
  }

  // What a signup is tagged with. Tags are how Shopify segments an email list.
  function signupTags(prefs) {
    var tags = ['newsletter', '31-nights-' + YEAR];
    ((prefs && prefs.tastes) || []).forEach(function (t) { if (TASTES[t]) tags.push('31n-taste-' + t); });
    if (prefs && LEVELS[prefs.level]) tags.push('31n-level-' + prefs.level);
    if (prefs && prefs.skipSeen) tags.push('31n-deep-cuts');
    ((prefs && prefs.eras) || []).forEach(function (e) { if (ERAS.indexOf(e) >= 0) tags.push('31n-era-' + e); });
    if (prefs) tags.push('31n-saint-' + verdict(prefs).key);
    return tags;
  }

  var core = { build: build, swap: swap, rank: rank, encode: encode, decode: decode, poolFor: poolFor, available: available, ERAS: ERAS, eraOf: eraOf, weekday: weekday, verdict: verdict, badges: badges, ics: ics, letterboxdCsv: letterboxdCsv, signupTags: signupTags, TASTES: TASTES, LEVELS: LEVELS, NIGHTS: NIGHTS, YEAR: YEAR };
  if (typeof module !== 'undefined' && module.exports) { module.exports = core; return; }
  root.SS31 = core;

  /* ---------------- the page ---------------- */

  var el = document.getElementById('ss31');
  if (!el) return;
  var KEY = 'ss31-2026';
  var DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var state = load();
  var films = [], byN = {};

  function load() {
    try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch (e) { return {}; }
  }
  function save() {
    if (state.guest) return;
    try { localStorage.setItem(KEY, JSON.stringify(state)); } catch (e) { /* private mode: the month just won't persist */ }
  }
  function $(sel, from) { return (from || el).querySelector(sel); }
  function $$(sel, from) { return [].slice.call((from || el).querySelectorAll(sel)); }
  function h(tag, attrs, kids) {
    var node = document.createElement(tag);
    Object.keys(attrs || {}).forEach(function (k) {
      if (attrs[k] === false || attrs[k] == null) return;
      if (k === 'text') node.textContent = attrs[k];
      else if (k.slice(0, 2) === 'on') node.addEventListener(k.slice(2), attrs[k]);
      else node.setAttribute(k, attrs[k] === true ? '' : attrs[k]);
    });
    (kids || []).forEach(function (kid) { if (kid) node.appendChild(typeof kid === 'string' ? document.createTextNode(kid) : kid); });
    return node;
  }
  function track(name, data) {
    try { if (root.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish('ss31_' + name, data || {}); } catch (e) { /* analytics never breaks the page */ }
  }

  // Which night is it? 0 before October, 32 after Halloween.
  function tonight() {
    var now = new Date();
    if (now < new Date(YEAR, 9, 1)) return 0;
    if (now >= new Date(YEAR, 10, 1, 5)) return 32; // the small hours of 1 Nov still count as Halloween night
    return now.getMonth() === 10 ? 31 : now.getDate();
  }

  // A real radio group: Tab lands on it once, arrow keys move the rating, and
  // focus comes back to the same halo after the card redraws.
  function haloRow(night, value, onSet) {
    var row = h('div', { class: 'ss31-halos', role: 'radiogroup', 'aria-label': 'Rate night ' + night + ' in halos' });
    var set = function (score) { refocus = { night: night, score: score || 1 }; onSet(score); };
    for (var i = 1; i <= 5; i++) (function (score) {
      row.appendChild(h('button', {
        type: 'button', class: 'ss31-halo' + (score <= value ? ' is-on' : ''), role: 'radio', 'data-score': score,
        tabindex: score === (value || 1) ? '0' : '-1',
        'aria-checked': score === value ? 'true' : 'false', 'aria-label': score + (score === 1 ? ' halo' : ' halos'),
        onclick: function () { set(score === value ? 0 : score); },
        onkeydown: function (e) {
          var step = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1 }[e.key];
          if (e.key === 'Home') step = 1 - score; if (e.key === 'End') step = 5 - score;
          if (!step) return;
          e.preventDefault();
          set(Math.min(5, Math.max(1, score + step)));
        },
      }));
    })(i);
    return row;
  }
  var refocus = null;

  var KIND = { slasher: 'Slasher', supernatural: 'Supernatural', possession: 'Possession', 'found-footage': 'Found footage', folk: 'Folk horror', creature: 'Creature feature',
    zombie: 'Zombie', vampire: 'Vampire', psychological: 'Psychological', comedy: 'Horror comedy', classic: 'Before 1980', 'sci-fi': 'Sci-fi horror', gateway: 'Gateway', anthology: 'Anthology', international: 'International' };

  // Each night is a ticket: a numbered stub on the left, the feature on the right.
  function card(night) {
    var film = byN[state.nights[night - 1]];
    var log = (state.log || {})[night] || {};
    var now = tonight();
    var meter = h('span', { class: 'ss31-scare', role: 'img', 'aria-label': 'Scare ' + film.i + ' of 5' });
    for (var d = 1; d <= 5; d++) meter.appendChild(h('i', { class: d <= film.i ? 'is-on' : '' }));
    return h('article', { class: 'ss31-card' + (log.done ? ' is-done' : '') + (night === now ? ' is-tonight' : '') + (night === 31 ? ' is-finale' : ''), id: 'ss31-night-' + night }, [
      h('div', { class: 'ss31-card__stub', 'aria-hidden': 'true' }, [
        h('b', { text: night < 10 ? '0' + night : String(night) }),
        h('span', { text: DAYS[weekday(night)] }),
        h('span', { text: 'Oct ' + night }),
      ]),
      h('div', { class: 'ss31-card__body' }, [
        h('header', {}, [
          h('span', { class: 'ss31-card__night', text: night === 31 ? 'Halloween night' : 'Night ' + night }),
          h('span', { class: 'ss31-card__kind', text: KIND[film.g[0]] || '' }),
        ]),
        h('h3', { text: film.t }),
        h('p', { class: 'ss31-card__meta' }, [film.y + ' · ' + film.rt + ' min · ', h('span', { class: 'visually-hidden', text: 'Scare ' + film.i + ' of 5' }), meter]),
        h('p', { class: 'ss31-card__why', text: film.w }),
        h('div', { class: 'ss31-card__acts' }, [
          h('label', { class: 'ss31-check' }, [
            h('input', { type: 'checkbox', checked: !!log.done, onchange: function (e) { setLog(night, { done: e.target.checked }); } }),
            h('span', { text: log.done ? 'Survived' : 'Mark survived' }),
          ]),
          h('a', { href: 'https://www.justwatch.com/us/search?q=' + encodeURIComponent(film.t), target: '_blank', rel: 'noopener nofollow', text: 'Where to watch' }),
          !log.done && !state.dare && h('button', { type: 'button', class: 'ss31-swap', onclick: function () { doSwap(night); }, text: 'Swap' }),
          log.done && h('span', { class: 'ss31-stamp', 'aria-hidden': 'true', text: 'Survived' }),
        ]),
        log.done && haloRow(night, log.halo || 0, function (score) { setLog(night, { halo: score }); }),
      ]),
    ]);
  }

  function setLog(night, patch) {
    state.log = state.log || {};
    state.log[night] = Object.assign({}, state.log[night], patch);
    if (patch.done === false) delete state.log[night];
    save(); draw();
    if (patch.done) track('night_survived', { night: night });
    if (patch.halo) track('halo_rating', { night: night, halos: patch.halo, film: byN[state.nights[night - 1]].t });
  }

  function doSwap(night) {
    state.swaps = (state.swaps || 0) + 1;
    state.nights = swap(films, state.prefs, state.nights, night, (state.prefs.seed || 1) * 131 + state.swaps);
    save(); draw();
    track('swap', { night: night });
  }

  function survived() { return Object.keys(state.log || {}).filter(function (k) { return state.log[k].done; }).length; }

  function streak() { // consecutive survived nights ending tonight (or last night)
    var n = Math.min(tonight(), 31), count = 0;
    if (n > 0 && !((state.log || {})[n] || {}).done) n--;
    while (n > 0 && ((state.log || {})[n] || {}).done) { count++; n--; }
    return count;
  }

  function draw() {
    var has = state.nights && state.nights.length === NIGHTS;
    el.classList.toggle('has-month', !!has);
    $('[data-quiz]').hidden = !!has && !el.classList.contains('is-editing');
    $('[data-month]').hidden = !has;
    if (!has) return;

    var done = survived(), now = tonight();
    var month = state.nights.map(function (n) { return byN[n]; });
    $('[data-owner]').textContent = state.prefs.name ? state.prefs.name + '’s' : 'Your';

    // verdict: a dared month has no quiz answers behind it, so it shows who dared them instead
    var box = $('[data-verdict]');
    box.replaceChildren();
    if (state.dare) {
      box.appendChild(h('p', { class: 'ss31-verdict__title', text: 'A dare is a dare' }));
      box.appendChild(h('p', { text: (state.dare === true ? 'Somebody' : state.dare) + ' handed you this month. Same 31 films, no swaps. Survive more nights than they do.' }));
    } else if (!state.guest) {
      var v = verdict(state.prefs);
      box.appendChild(h('p', { class: 'ss31-eyebrow', text: 'Your verdict' }));
      box.appendChild(h('p', { class: 'ss31-verdict__title', text: v.title }));
      box.appendChild(h('p', { text: v.line + ' ' + v.vow }));
    }
    box.hidden = !box.childNodes.length;

    var row = $('[data-badges]');
    row.replaceChildren();
    badges(state.log, month).forEach(function (b) {
      row.appendChild(h('li', { class: b.earned ? 'is-earned' : '', title: b.how }, [h('b', { text: b.name }), h('span', { text: b.how })]));
    });

    // The signup block lives at the foot of the page for visitors with no month.
    // Once there is a month it moves up beside it and becomes "Seal your month".
    var seal = $('[data-seal]'), slot = $('[data-seal-slot]');
    if (seal && slot && seal.parentNode !== slot && !state.guest) slot.appendChild(seal);
    if (seal) {
      seal.classList.toggle('is-sealed', !!state.sealed);
      $('[data-seal-head]', seal).textContent = state.sealed ? 'Your month is sealed' : 'Seal your month';
      $('[data-seal-text]', seal).textContent = state.sealed
        ? 'The printable checklist is unlocked below, and the week ahead lands every Sunday.'
        : 'Sealing unlocks the printable checklist for the fridge, puts the week ahead in your inbox every Sunday, and tells us your taste so we only send you your kind of horror.';
      var sealBtn = $('button[type="submit"]', seal);
      if (sealBtn && !state.guest) sealBtn.textContent = 'Seal my month';
      var tagField = $('input[name="contact[tags]"]', seal);
      if (tagField && !state.dare) tagField.value = signupTags(state.prefs).join(',');
    }
    var uni = $('[data-uniform]'), uniSlot = $('[data-uniform-slot]');
    if (uni && uniSlot && uni.parentNode !== uniSlot && !state.guest) uniSlot.appendChild(uni);
    var printBtn = $('[data-act="print"]');
    printBtn.disabled = !state.sealed;
    printBtn.textContent = state.sealed ? 'Print my checklist' : 'Print my checklist (seal your month first)';

    var sheet = $('[data-print]');
    sheet.replaceChildren(h('h2', { text: (state.prefs.name ? state.prefs.name + '’s ' : '') + '31 Nights of Screams' }),
      h('p', { text: 'Tick each night you survive. Rate it in halos, one to five. screamingsaints.com/31' }));
    var list = h('ol', {});
    month.forEach(function (f, i) {
      list.appendChild(h('li', {}, [h('i', {}), h('b', { text: DAYS[weekday(i + 1)] + ' ' + (i + 1) }), h('span', { text: f.t + ' (' + f.y + ') · ' + f.rt + ' min' }), h('em', { text: '○ ○ ○ ○ ○' })]));
    });
    sheet.appendChild(list);
    $('[data-rank]').textContent = rank(done);
    $('[data-count]').textContent = done + ' of 31';
    $('[data-streak]').textContent = String(streak());
    $('[data-bar]').style.transform = 'scaleX(' + (done / 31) + ')';
    var widened = $('[data-widened]');
    widened.hidden = !state.widened;
    if (state.widened) {
      widened.textContent = {
        seen: 'There were not 31 films you may not have seen in that corner of the library, so a few well known ones are in the mix.',
        band: 'That taste and scare level together ran short of 31 films, so a few nights sit one notch outside your scare level.',
        any: 'That combination was too narrow to fill a month, so other kinds of horror at your scare level fill the gaps.',
      }[state.widened];
    }

    var spot = $('[data-tonight]');
    spot.replaceChildren();
    if (now === 0) {
      var days = Math.ceil((new Date(YEAR, 9, 1) - new Date()) / 864e5);
      spot.appendChild(h('p', { class: 'ss31-tonight__wait', text: 'Night one is Thursday 1 October. ' + days + (days === 1 ? ' day' : ' days') + ' to go. Halloween lands on a Saturday this year.' }));
    } else if (now <= 31) {
      spot.appendChild(h('p', { class: 'ss31-eyebrow', text: now === 31 ? 'Tonight is Halloween' : 'Tonight is night ' + now }));
      spot.appendChild(card(now));
    } else {
      spot.appendChild(h('p', { class: 'ss31-tonight__wait', text: 'October is over. You finished as a ' + rank(done) + ' with ' + done + ' of 31 nights survived.' }));
    }

    var grid = $('[data-grid]');
    grid.replaceChildren();
    for (var night = 1; night <= NIGHTS; night++) grid.appendChild(card(night));
    if (refocus) {
      var back = $('[data-grid] #ss31-night-' + refocus.night + ' [data-score="' + refocus.score + '"]');
      refocus = null;
      if (back) back.focus({ preventScroll: true });
    }
  }

  /* ---- quiz ---- */

  function readQuiz() {
    return {
      tastes: $$('[name="ss31-taste"]:checked').map(function (i) { return i.value; }),
      level: ($('[name="ss31-level"]:checked') || {}).value || 'creepy',
      eras: $$('[name="ss31-era"]:checked').map(function (i) { return i.value; }),
      skipSeen: !!($('[name="ss31-skip"]') || {}).checked,
      name: (($('[name="ss31-name"]') || {}).value || '').trim().slice(0, 18),
      seed: Math.floor(Math.random() * 1e9) + 1,
    };
  }

  function generate() {
    var prefs = readQuiz();
    var logged = survived();
    if (logged && !confirm('Building a new month clears the ' + logged + (logged === 1 ? ' night' : ' nights') + ' you have marked as survived. Go ahead?')) return;
    var out = build(films, prefs);
    if (out.error) { countFilms(); $('[data-short]').scrollIntoView({ block: 'center' }); return; }
    state = { prefs: prefs, nights: out.nights, widened: out.widened, log: {}, swaps: 0, sealed: !!state.sealed }; // sealing belongs to the person, not the month
    el.classList.remove('is-editing');
    save(); draw();
    track('calendar_built', { tastes: prefs.tastes.join(','), level: prefs.level, skip_seen: prefs.skipSeen });
    $('[data-month]').scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  }

  // Runs on every quiz change: says how many films are in play, and blocks a
  // month that the chosen decades cannot fill.
  function countFilms() {
    if (!films.length) return;
    var prefs = readQuiz(), n = available(films, prefs), short = n < NIGHTS;
    var note = $('[data-short]');
    note.hidden = !short;
    if (short) note.textContent = 'Only ' + n + ' films from ' + (prefs.eras.length === 1 ? 'that decade' : 'those decades') + ' fit that scare level, and a month needs 31. Add another decade or change the scare level.';
    $('[data-act="build"]').disabled = short;
    $('[data-era-any]').checked = !prefs.eras.length;
  }

  /* ---- poster ---- */

  /* The poster is laid out like a film one-sheet: crest and halo, a "presents"
   * line, the title, the month as the cast list, a survived stamp, and a
   * billing block. On the Story size everything that matters sits between
   * y=250 and y=1660, because Instagram covers the rest with its own buttons. */
  var crest = null;
  (function () {
    var src = el.getAttribute('data-crest');
    if (!src) return;
    var img = new Image();
    img.crossOrigin = 'anonymous'; // a canvas that has drawn a cross-origin image without this can never be exported
    img.onload = function () { crest = img; };
    img.src = src;
  })();

  function poster(tall) {
    var W = 1080, H = tall ? 1920 : 1350;
    var L = tall
      ? { crestY: 372, crestR: 96, presents: 520, title: 668, titleSize: 164, sub: 770, subSize: 112, rule: 794, verdict: 850, listTop: 896, listBottom: 1450, stamp: 1508, badges: 1556, tag1: 1600, tag2: 1640, prize: 1680, bill: 1724, billGap: 30 }
      : { crestY: 118, crestR: 66, presents: 222, title: 346, titleSize: 132, sub: 430, subSize: 92, rule: 450, verdict: 498, listTop: 536, listBottom: 1070, stamp: 1122, badges: 1166, tag1: 1204, tag2: 1242, prize: 1278, bill: 1306, billGap: 26 };
    var c = document.createElement('canvas'); c.width = W; c.height = H;
    var x = c.getContext('2d');
    var head = '"Bebas Neue", "Arial Narrow", Impact, sans-serif', body = 'Inter, "Helvetica Neue", Arial, sans-serif';
    var g = rng(7), i;

    // atmosphere: black, a red sky, shafts of light from above, fog on the floor
    x.fillStyle = '#060607'; x.fillRect(0, 0, W, H);
    var sky = x.createRadialGradient(W / 2, L.crestY, 30, W / 2, L.crestY, W * 1.05);
    sky.addColorStop(0, 'rgba(193,18,31,.5)'); sky.addColorStop(.45, 'rgba(120,10,20,.2)'); sky.addColorStop(1, 'rgba(0,0,0,0)');
    x.fillStyle = sky; x.fillRect(0, 0, W, H);
    x.save(); x.globalCompositeOperation = 'lighter';
    [[-0.62, 0.2], [-0.3, 0.13], [0.04, 0.17], [0.34, 0.12], [0.66, 0.19]].forEach(function (ray) {
      for (var f = 0; f < 5; f++) { // five feathered layers per shaft, so the edges melt instead of cutting
        var spread = ray[1] * (1 - f * 0.17);
        var beam = x.createLinearGradient(W / 2, -60, W / 2 + ray[0] * H, H * 0.78);
        beam.addColorStop(0, 'rgba(255,225,190,.024)'); beam.addColorStop(1, 'rgba(255,225,190,0)');
        x.fillStyle = beam; x.beginPath(); x.moveTo(W / 2 - 18, -60); x.lineTo(W / 2 + 18, -60);
        x.lineTo(W / 2 + (ray[0] + spread) * H, H * 0.82); x.lineTo(W / 2 + (ray[0] - spread) * H, H * 0.82); x.closePath(); x.fill();
      }
    });
    x.restore();
    for (i = 0; i < 7; i++) {
      var fx = g() * W, fy = H - g() * H * 0.2, fr = 240 + g() * 320, fog = x.createRadialGradient(fx, fy, 10, fx, fy, fr);
      fog.addColorStop(0, 'rgba(190,190,200,.075)'); fog.addColorStop(1, 'rgba(190,190,200,0)');
      x.fillStyle = fog; x.fillRect(0, 0, W, H);
    }

    // film strip down both edges
    [0, W - 58].forEach(function (sx) {
      x.fillStyle = 'rgba(0,0,0,.72)'; x.fillRect(sx, 0, 58, H);
      x.fillStyle = 'rgba(234,230,222,.13)';
      for (var py = 22; py < H; py += 62) { rounded(x, sx + 15, py, 28, 20, 5); x.fill(); }
    });
    x.strokeStyle = 'rgba(234,230,222,.28)'; x.lineWidth = 2; x.strokeRect(74, 16, W - 148, H - 32);

    // crest under a halo
    x.save(); x.shadowColor = 'rgba(246,130,0,.6)'; x.shadowBlur = 60;
    x.beginPath(); x.arc(W / 2, L.crestY, L.crestR, 0, Math.PI * 2); x.fillStyle = '#0b0605'; x.fill(); x.restore();
    if (crest) { x.save(); x.beginPath(); x.arc(W / 2, L.crestY, L.crestR, 0, Math.PI * 2); x.clip(); x.drawImage(crest, W / 2 - L.crestR, L.crestY - L.crestR, L.crestR * 2, L.crestR * 2); x.restore(); }
    x.strokeStyle = '#c1121f'; x.lineWidth = 4; x.beginPath(); x.arc(W / 2, L.crestY, L.crestR, 0, Math.PI * 2); x.stroke();
    x.save(); x.translate(W / 2, L.crestY - L.crestR - 12); x.rotate(-0.07); x.shadowColor = 'rgba(255,205,96,.95)'; x.shadowBlur = 26;
    x.strokeStyle = 'rgba(255,214,120,.98)'; x.lineWidth = 7; x.beginPath(); x.ellipse(0, 0, L.crestR * 0.92, L.crestR * 0.2, 0, 0, Math.PI * 2); x.stroke(); x.restore();

    // titles
    x.textAlign = 'center';
    x.fillStyle = '#e61e2d'; x.font = '700 25px ' + body;
    x.fillText(spaced(((state.prefs.name || 'Screaming Saints') + ' presents').toUpperCase()), W / 2, L.presents);
    x.save(); x.shadowColor = 'rgba(193,18,31,.75)'; x.shadowBlur = 38;
    x.fillStyle = '#f0deaa'; x.font = L.titleSize + 'px ' + head; x.fillText('31 NIGHTS', W / 2, L.title);
    x.fillStyle = '#e61e2d'; x.font = L.subSize + 'px ' + head; x.fillText('OF SCREAMS', W / 2, L.sub);
    x.restore();
    for (i = 0; i < 520; i++) { // worn print: specks of the background over the lettering
      x.fillStyle = 'rgba(14,6,7,' + (0.35 + g() * 0.5) + ')';
      x.fillRect(180 + g() * 720, L.title - L.titleSize + g() * (L.sub - L.title + L.titleSize), 1 + g() * 3.2, 1 + g() * 2.4);
    }
    x.fillStyle = '#c1121f'; x.fillRect(W / 2 - 180, L.rule, 360, 5);
    x.fillStyle = 'rgba(255,205,96,.96)'; x.font = '38px ' + head;
    x.fillText(spaced(state.dare || state.guest ? 'ONE HORROR FILM EVERY NIGHT OF OCTOBER' : verdict(state.prefs).title.toUpperCase()), W / 2, L.verdict);

    // the month, billed like a cast list
    var rows = 16, rowH = (L.listBottom - L.listTop) / rows, left = 112, colW = (W - left * 2) / 2, size = Math.min(27, rowH * 0.62);
    x.textAlign = 'left';
    for (var n = 1; n <= NIGHTS; n++) {
      var col = n <= rows ? 0 : 1, r = n <= rows ? n - 1 : n - rows - 1;
      var px = left + col * (colW + 8), py = L.listTop + r * rowH + rowH * 0.72;
      var film = byN[state.nights[n - 1]], entry = (state.log || {})[n] || {};
      x.fillStyle = 'rgba(234,230,222,.1)'; x.fillRect(px, py + rowH * 0.26, colW - 16, 1);
      x.fillStyle = n === 31 ? '#e61e2d' : 'rgba(230,30,45,.78)'; x.font = (size + 7) + 'px ' + head;
      x.fillText(n < 10 ? '0' + n : String(n), px, py + 2);
      x.fillStyle = n === 31 ? '#f0deaa' : entry.done ? 'rgba(234,230,222,.5)' : '#eae6de';
      x.font = (n === 31 ? '700 ' : '500 ') + size + 'px ' + body;
      var halos = entry.done ? entry.halo || 0 : 0;
      var title = fit(x, film.t, colW - 78 - (halos ? halos * 17 + 8 : 0));
      x.fillText(title, px + 54, py);
      if (entry.done) { x.fillStyle = '#c1121f'; x.fillRect(px + 52, py - size * 0.36, x.measureText(title).width + 4, 3); }
      for (var k = 0; k < halos; k++) { // their rating, in halos
        x.save(); x.translate(px + colW - 26 - k * 17, py - size * 0.34); x.rotate(-0.17); x.shadowColor = 'rgba(255,205,96,.9)'; x.shadowBlur = 6;
        x.strokeStyle = 'rgb(255,205,96)'; x.lineWidth = 2.2; x.beginPath(); x.ellipse(0, 0, 6.5, 2.9, 0, 0, Math.PI * 2); x.stroke(); x.restore();
      }
    }

    // survived stamp
    var done = survived();
    var words = done ? done + ' OF 31 SURVIVED  ·  ' + rank(done).toUpperCase() : 'NIGHT ONE  ·  THURSDAY 1 OCTOBER';
    x.save(); x.translate(W / 2, L.stamp); x.rotate(-0.035); x.textAlign = 'center';
    x.font = '46px ' + head; var sw = x.measureText(spaced(words)).width + 56;
    x.strokeStyle = 'rgba(230,30,45,.92)'; x.lineWidth = 4; rounded(x, -sw / 2, -40, sw, 58, 8); x.stroke();
    x.fillStyle = 'rgba(230,30,45,.95)'; x.fillText(spaced(words), 0, 4);
    for (i = 0; i < 90; i++) { x.fillStyle = 'rgba(8,8,9,.85)'; x.fillRect(-sw / 2 + g() * sw, -42 + g() * 62, 1 + g() * 4, 1 + g() * 2); }
    x.restore();

    x.textAlign = 'center';
    var earned = badges(state.log, state.nights.map(function (m) { return byN[m]; })).filter(function (bd) { return bd.earned; }).map(function (bd) { return bd.name.toUpperCase(); });
    if (earned.length > 4) earned = earned.slice(0, 4).concat('+' + (earned.length - 4) + ' MORE');
    if (earned.length) { x.fillStyle = 'rgba(255,205,96,.9)'; x.font = '600 19px ' + body; x.fillText(fit(x, spaced(earned.join('   ·   ')), W - 230), W / 2, L.badges); }
    // the pitch: a stranger has to learn, in two lines, that theirs would be different and it is free
    x.fillStyle = '#f0deaa'; x.font = '38px ' + head;
    x.fillText(spaced('YOURS WILL BE DIFFERENT'), W / 2, L.tag1);
    x.fillStyle = '#eae6de'; x.font = '31px ' + head;
    x.fillText(spaced('3 QUESTIONS  ·  31 FILMS FOR YOUR TASTE  ·  FREE AT SCREAMINGSAINTS.COM/31'), W / 2, L.tag2);
    if (el.getAttribute('data-contest') === 'true') {
      x.fillStyle = 'rgb(255,205,96)'; x.font = '31px ' + head;
      x.fillText(spaced('SEAL YOUR MONTH AND YOU COULD WIN A HOODIE'), W / 2, L.prize);
    }

    // billing block
    x.fillStyle = 'rgba(234,230,222,.5)'; x.font = '24px ' + head;
    x.fillText(spaced('A SCREAMING SAINTS PRODUCTION    ONE HORROR FILM A NIGHT    RATED IN HALOS'), W / 2, L.bill);
    x.fillText(spaced('OCTOBER 2026    HALLOWEEN FALLS ON A SATURDAY    @SCREAMINGSAINTS'), W / 2, L.bill + L.billGap);

    // grain and a hard vignette over everything
    for (i = 0; i < 14000; i++) { x.fillStyle = 'rgba(234,230,222,' + (g() * 0.055) + ')'; x.fillRect(g() * W, g() * H, 1.5, 1.5); }
    var vig = x.createRadialGradient(W / 2, H * 0.46, H * 0.3, W / 2, H * 0.46, H * 0.78);
    vig.addColorStop(0, 'rgba(0,0,0,0)'); vig.addColorStop(1, 'rgba(0,0,0,.72)');
    x.fillStyle = vig; x.fillRect(0, 0, W, H);
    return c;
  }
  function rounded(x, rx, ry, w, hh, r) {
    x.beginPath(); x.moveTo(rx + r, ry); x.arcTo(rx + w, ry, rx + w, ry + hh, r); x.arcTo(rx + w, ry + hh, rx, ry + hh, r);
    x.arcTo(rx, ry + hh, rx, ry, r); x.arcTo(rx, ry, rx + w, ry, r); x.closePath();
  }
  function spaced(s) { return s.split('').join(' '); }
  function fit(x, s, max) {
    if (x.measureText(s).width <= max) return s;
    while (s.length > 4 && x.measureText(s + '…').width > max) s = s.slice(0, -1);
    return s.replace(/[\s,:]+$/, '') + '…';
  }

  /* The poster opens in a panel instead of going straight to a download. Most
   * visitors arrive inside Instagram's or TikTok's built-in browser, where the
   * share sheet and file downloads are often blocked. A plain image that can be
   * pressed and held to save works in all of them. Share and Download are
   * offered on top of that wherever the browser allows them. */
  function sharePoster(tall) {
    var ready = document.fonts && document.fonts.load ? Promise.all([document.fonts.load('150px "Bebas Neue"'), document.fonts.load('600 26px Inter')]) : Promise.resolve();
    ready.catch(function () {}).then(function () {
      var canvas = poster(tall);
      track('poster', { format: tall ? 'story' : 'feed' });
      canvas.toBlob(function (blob) {
        var file = blob && typeof File === 'function' ? new File([blob], '31-nights-of-screams.png', { type: 'image/png' }) : null;
        var canSend = !!(file && navigator.canShare && navigator.canShare({ files: [file] }));
        var url = blob ? URL.createObjectURL(blob) : canvas.toDataURL('image/png');
        var box = h('dialog', { class: 'ss31-modal', 'aria-label': 'Your poster' }, [
          h('p', { class: 'ss31-modal__how', text: canSend ? 'Tap Share, or press and hold the poster to save it. Then add it to your Story and tag @screamingsaints.' : 'Download the poster, or copy the link to send someone your month. Post it and tag @screamingsaints.' }),
          h('img', { src: url, alt: 'Your 31 Nights of Screams poster', class: tall ? 'is-tall' : '' }),
          h('div', { class: 'ss31-share__acts' }, [
            canSend && h('button', { type: 'button', class: 'ss31-btn', text: 'Share', onclick: function () {
              navigator.share({ files: [file], title: '31 Nights of Screams', text: shareText() + ' ' + shareUrl() }).then(function () { track('poster_shared'); }, function () {});
            } }),
            h('a', { class: 'ss31-btn ss31-btn--ghost', href: url, download: '31-nights-of-screams.png', text: 'Download', onclick: function () { track('poster_download'); } }),
            h('button', { type: 'button', class: 'ss31-btn ss31-btn--ghost', text: 'Copy link', onclick: function (e) { copyLink(e.currentTarget); } }),
            h('button', { type: 'button', class: 'ss31-btn ss31-btn--ghost', text: 'Close', onclick: function () { box.close(); } }),
          ]),
        ]);
        box.addEventListener('close', function () { box.remove(); if (blob) URL.revokeObjectURL(url); });
        box.addEventListener('click', function (e) { if (e.target === box) box.close(); });
        el.appendChild(box);
        if (box.showModal) box.showModal(); else box.setAttribute('open', '');
      }, 'image/png');
    });
  }

  function shareText() {
    var who = state.dare ? '' : verdict(state.prefs).title + '. ';
    return 'My 31 Nights of Screams: a horror film every night of October. ' + who + 'I dare you to survive my month.';
  }

  // Platforms that accept a link with words already typed. Instagram and TikTok
  // do not: they only take the poster, through the panel above.
  function shareTo(where) {
    var u = encodeURIComponent(shareUrl()), t = encodeURIComponent(shareText());
    var to = {
      x: 'https://twitter.com/intent/tweet?text=' + t + '&url=' + u,
      facebook: 'https://www.facebook.com/sharer/sharer.php?u=' + u,
      threads: 'https://www.threads.net/intent/post?text=' + t + '%20' + u,
      whatsapp: 'https://wa.me/?text=' + t + '%20' + u,
      sms: 'sms:?&body=' + t + '%20' + u,
    }[where];
    if (!to) return;
    track('share_link', { to: where });
    if (where === 'sms') location.href = to; else window.open(to, '_blank', 'noopener');
  }

  function shareUrl() {
    // In the query string, not after a #: Facebook and several in-app browsers drop the # part.
    var name = state.prefs && state.prefs.name ? '&n=' + encodeURIComponent(state.prefs.name) : '';
    return location.origin + location.pathname + '?c=' + encode(state.nights) + name;
  }

  function download(name, type, text, what) {
    var a = h('a', { href: URL.createObjectURL(new Blob([text], { type: type + ';charset=utf-8' })), download: name });
    document.body.appendChild(a); a.click(); a.remove();
    track('export', { what: what });
  }

  function copyLink(btn) {
    var url = shareUrl();
    var done = function () { var t = btn.textContent; btn.textContent = 'Link copied'; setTimeout(function () { btn.textContent = t; }, 1800); track('link_copied'); };
    if (navigator.clipboard) navigator.clipboard.writeText(url).then(done, function () { prompt('Copy this link', url); });
    else prompt('Copy this link', url);
  }

  /* ---- start ---- */

  function openShared() {
    var where = location.search + '&' + location.hash; // links made before 21 Sep carried the month after a #
    var m = /[?#&]c=([0-9a-z.]+)/i.exec(where);
    if (!m) return;
    var nights = decode(m[1], films);
    if (!nights) return;
    var name = /[?#&]n=([^&#]*)/.exec(where);
    var mine = state.nights && encode(state.nights) === m[1];
    if (mine) return;
    // Someone else's month: show it without touching this visitor's own saved one.
    var saved = state;
    state = { prefs: { name: name ? decodeURIComponent(name[1]).slice(0, 18) : '', level: 'creepy', tastes: [] }, nights: nights, log: {}, guest: true };
    el.classList.add('is-guest');
    var from = state.prefs.name;
    $('[data-guest-from]').textContent = from ? from + ' shared this month with you.' : 'Somebody shared this month with you.';
    $('[data-guest-own]').addEventListener('click', function () { history.replaceState(null, '', location.pathname); location.reload(); });
    $('[data-guest-dare]').addEventListener('click', function () {
      if (saved.nights && !confirm('You already have a month on this device. Taking the dare replaces it. Go ahead?')) return;
      state = { prefs: { name: '', level: 'creepy', tastes: [] }, nights: nights, log: {}, dare: from || true, sealed: !!saved.sealed };
      save();
      history.replaceState(null, '', location.pathname);
      location.reload();
    });
    track('shared_opened');
  }

  el.addEventListener('change', function (e) {
    if (!e.target.closest('[data-quiz]')) return;
    if (e.target.hasAttribute('data-era-any') && e.target.checked) $$('[name="ss31-era"]').forEach(function (i) { i.checked = false; });
    countFilms();
  });

  el.addEventListener('click', function (e) {
    var t = e.target.closest('[data-act]');
    if (!t) return;
    var act = t.getAttribute('data-act');
    if (act === 'build') generate();
    if (act === 'story') sharePoster(true);
    if (act === 'feed') sharePoster(false);
    if (act === 'link') copyLink(t);
    if (act === 'to') shareTo(t.getAttribute('data-to'));
    if (act === 'ics') download('31-nights-of-screams.ics', 'text/calendar', ics(state.nights.map(function (n) { return byN[n]; }), location.origin + location.pathname), 'calendar');
    if (act === 'letterboxd') download('31-nights-of-screams-letterboxd.csv', 'text/csv', letterboxdCsv(state.nights.map(function (n) { return byN[n]; })), 'letterboxd');
    if (act === 'print' && state.sealed) { track('print'); window.print(); }
    if (act === 'redo') { el.classList.add('is-editing'); draw(); $('[data-quiz]').scrollIntoView({ block: 'start' }); }
    if (act === 'start') $('[data-quiz]').scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  fetch(el.getAttribute('data-films'))
    .then(function (r) { return r.json(); })
    .then(function (list) {
      films = list; byN = index(list);
      if (state.nights && !state.nights.every(function (n) { return byN[n]; })) state = {}; // library changed under an old save
      if (/[?&]reset=1\b/.test(location.search)) {
        var keepSeal = !!state.sealed;
        state = keepSeal ? { sealed: true } : {};
        save();
        history.replaceState(null, '', location.pathname);
      }
      openShared();
      if (!state.guest && state.nights && (/customer_posted=true/.test(location.search) || $('.ss31-form__ok'))) {
        if (!state.sealed) track('sealed', { saint: state.dare ? 'dare' : verdict(state.prefs).key });
        state.sealed = true; save();
      }
      el.classList.add('is-ready');
      countFilms();
      draw();
    })
    .catch(function () { $('[data-error]').hidden = false; });
})(typeof window !== 'undefined' ? window : globalThis);
